import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { createAsyncActions } from '@icebreaker/shared/store';
import type {
  AdminOverviewDto,
  AdminVerificationQueueItemDto,
  AdminVerificationQueueQuery,
  DecideOrganizationVerificationInput,
  SignedDocumentUrlDto,
  AdminUserFilter,
  AdminUserListDto,
  AdminUserListQuery,
  AdminUserRowDto,
  ApiErrorBody,
  DecideModerationInput,
  ModerationQueueItemDetailDto,
  ModerationQueueItemDto,
  ModerationQueueQuery,
  PaginatedResult,
  SignalRunResultDto,
} from '@icebreaker/shared';

/**
 * Admin panel durumu (Epic 11). Kuyruk kalemleri ve kullanıcı satırları admin'e
 * özel PROJEKSİYONLARDIR (katılımcı listesiyle aynı desen — attendees.slice);
 * mutasyon sonrası tazeleme `INVALIDATION_MAP` (`admin/*`) anahtarlarına göre
 * saga tarafından açıkça yapılır.
 */

// --- Async action üçlüleri ---------------------------------------------------

export const fetchAdminUsers = createAsyncActions<AdminUserListQuery, AdminUserListDto>(
  'admin/usersFetch',
);
export const suspendUser = createAsyncActions<
  { userId: string; reason: string },
  { userId: string }
>('admin/suspendUser');
export const unsuspendUser = createAsyncActions<
  { userId: string; reason: string },
  { userId: string }
>('admin/unsuspendUser');

export const fetchQueue = createAsyncActions<
  ModerationQueueQuery,
  PaginatedResult<ModerationQueueItemDto>
>('admin/queueFetch');
export const fetchQueueItem = createAsyncActions<string, ModerationQueueItemDetailDto>(
  'admin/queueItemFetch',
);
export const decideModeration = createAsyncActions<
  { itemId: string; input: DecideModerationInput },
  ModerationQueueItemDetailDto
>('admin/decideModeration');
export const runSignals = createAsyncActions<void, SignalRunResultDto>('admin/runSignals');
export const fetchOverview = createAsyncActions<void, AdminOverviewDto>('admin/overviewFetch');

/** Kurum doğrulama kuyruğu (Story 18.4). */
export const fetchVerificationQueue = createAsyncActions<
  AdminVerificationQueueQuery,
  PaginatedResult<AdminVerificationQueueItemDto>
>('admin/verificationQueueFetch');
export const decideOrganizationVerification = createAsyncActions<
  { requestId: string; input: DecideOrganizationVerificationInput },
  AdminVerificationQueueItemDto
>('admin/decideOrganizationVerification');
/**
 * Belgeye imzalı bağlantı SAGA üzerinden istenir (tech-lead Yüksek-2): bileşen
 * doğrudan API çağırmaz. Bağlantı üretimi denetim kaydı yazdığı için YALNIZ
 * tıklamayla tetiklenir; sonuç depoda tutulur ve satırda indirme bağlantısına
 * dönüşür (pop-up engelleyicisine takılan `window.open` yok).
 */
export const fetchVerificationDocument = createAsyncActions<string, SignedDocumentUrlDto>(
  'admin/verificationDocument',
);

// --- Durum -------------------------------------------------------------------

type Phase = 'idle' | 'loading' | 'succeeded' | 'failed';

/** readonly DTO'ların immer-dostu kopya tipleri (attendees.slice deseni). */
interface QueueItemState extends Omit<ModerationQueueItemDto, 'target'> {
  target: {
    type: ModerationQueueItemDto['target']['type'];
    id: string;
    label: string;
    reference: string | null;
  };
}
interface OverviewState {
  kpis: AdminOverviewDto['kpis'];
  signals: AdminOverviewDto['signals'][number][];
  health: AdminOverviewDto['health'][number][];
}

interface QueueDetailState extends Omit<ModerationQueueItemDetailDto, 'facts' | 'reportNotes'> {
  facts: { key: string; value: string }[];
  reportNotes: string[];
}

export interface AdminUsersFilterState {
  query: string;
  filter: AdminUserFilter;
  page: number;
}

export interface AdminState {
  users: {
    items: AdminUserRowDto[];
    page: number;
    total: number;
    hasNext: boolean;
    registeredTotal: number;
    newLast7Days: number;
    phase: Phase;
    error: ApiErrorBody | null;
    filters: AdminUsersFilterState;
  };
  queue: {
    items: QueueItemState[];
    page: number;
    total: number;
    hasNext: boolean;
    phase: Phase;
    error: ApiErrorBody | null;
  };
  detail: {
    item: QueueDetailState | null;
    phase: Phase;
    error: ApiErrorBody | null;
  };
  selectedItemId: string | null;
  overview: {
    data: OverviewState | null;
    phase: Phase;
    error: ApiErrorBody | null;
  };
  verificationQueue: {
    items: AdminVerificationQueueItemDto[];
    status: AdminVerificationQueueQuery['status'];
    page: number;
    total: number;
    hasNext: boolean;
    phase: Phase;
    error: ApiErrorBody | null;
  };
  /** Son istenen belge bağlantısı — TEK satır için tutulur (süreli ve denetimli). */
  verificationDocument: {
    requestId: string | null;
    url: string | null;
    expiresAt: string | null;
    phase: Phase;
    error: ApiErrorBody | null;
  };
  /** Askıya alma / karar / sinyal koşusu ortak mutasyon durumu. */
  mutation: { phase: Phase; error: ApiErrorBody | null };
  lastSignalRun: number | null;
}

const initialState: AdminState = {
  users: {
    items: [],
    page: 1,
    total: 0,
    hasNext: false,
    registeredTotal: 0,
    newLast7Days: 0,
    phase: 'idle',
    error: null,
    filters: { query: '', filter: 'all', page: 1 },
  },
  queue: { items: [], page: 1, total: 0, hasNext: false, phase: 'idle', error: null },
  detail: { item: null, phase: 'idle', error: null },
  selectedItemId: null,
  overview: { data: null, phase: 'idle', error: null },
  verificationQueue: {
    items: [],
    status: 'open',
    page: 1,
    total: 0,
    hasNext: false,
    phase: 'idle',
    error: null,
  },
  verificationDocument: { requestId: null, url: null, expiresAt: null, phase: 'idle', error: null },
  mutation: { phase: 'idle', error: null },
  lastSignalRun: null,
};

const slice = createSlice({
  name: 'admin',
  initialState,
  reducers: {
    usersFilterChanged(state, action: PayloadAction<Partial<AdminUsersFilterState>>) {
      state.users.filters = { ...state.users.filters, ...action.payload };
    },
    queueItemSelected(state, action: PayloadAction<string | null>) {
      state.selectedItemId = action.payload;
      state.detail = { item: null, phase: 'idle', error: null };
    },
    mutationReset(state) {
      state.mutation = { phase: 'idle', error: null };
    },
  },
  extraReducers: (builder) => {
    builder
      // Kullanıcı listesi
      .addCase(fetchAdminUsers.request, (state) => {
        state.users.phase = 'loading';
        state.users.error = null;
      })
      .addCase(fetchAdminUsers.success, (state, action) => {
        const data = action.payload;
        state.users.phase = 'succeeded';
        state.users.items = data.items.map((item) => ({ ...item }));
        state.users.page = data.page;
        state.users.total = data.total;
        state.users.hasNext = data.hasNext;
        state.users.registeredTotal = data.registeredTotal;
        state.users.newLast7Days = data.newLast7Days;
      })
      .addCase(fetchAdminUsers.failure, (state, action) => {
        state.users.phase = 'failed';
        state.users.error = action.payload;
      })
      // Askıya alma / kaldırma — satır yerinde güncellenir, liste saga ile tazelenir.
      .addCase(suspendUser.request, (state) => {
        state.mutation = { phase: 'loading', error: null };
      })
      .addCase(suspendUser.success, (state, action) => {
        state.mutation.phase = 'succeeded';
        const row = state.users.items.find((item) => item.id === action.payload.userId);
        if (row) {
          // Kesin zaman sunucudadır; satır bir sonraki fetch'te gerçek değeri alır.
          row.status = 'suspended';
          row.suspendedAt = row.suspendedAt ?? '';
        }
      })
      .addCase(suspendUser.failure, (state, action) => {
        state.mutation = { phase: 'failed', error: action.payload };
      })
      .addCase(unsuspendUser.request, (state) => {
        state.mutation = { phase: 'loading', error: null };
      })
      .addCase(unsuspendUser.success, (state, action) => {
        state.mutation.phase = 'succeeded';
        const row = state.users.items.find((item) => item.id === action.payload.userId);
        if (row) {
          row.status = 'active';
          row.suspendedAt = null;
        }
      })
      .addCase(unsuspendUser.failure, (state, action) => {
        state.mutation = { phase: 'failed', error: action.payload };
      })
      // Kuyruk
      .addCase(fetchQueue.request, (state) => {
        state.queue.phase = 'loading';
        state.queue.error = null;
      })
      .addCase(fetchQueue.success, (state, action) => {
        const data = action.payload;
        state.queue.phase = 'succeeded';
        state.queue.items = data.items.map(cloneQueueItem);
        state.queue.page = data.page;
        state.queue.total = data.total;
        state.queue.hasNext = data.hasNext;
        if (
          state.selectedItemId === null ||
          !data.items.some((item) => item.id === state.selectedItemId)
        ) {
          state.selectedItemId = data.items[0]?.id ?? null;
        }
      })
      .addCase(fetchQueue.failure, (state, action) => {
        state.queue.phase = 'failed';
        state.queue.error = action.payload;
      })
      // Detay
      .addCase(fetchQueueItem.request, (state) => {
        state.detail.phase = 'loading';
        state.detail.error = null;
      })
      .addCase(fetchQueueItem.success, (state, action) => {
        state.detail.phase = 'succeeded';
        state.detail.item = cloneDetail(action.payload);
        // Liste satırı da senkronlanır — claim kararı sonrası tazelemede (Blok 6
        // dikişi) kalem listede "resolved" görünür, yeniden liste çekilmez.
        const index = state.queue.items.findIndex((item) => item.id === action.payload.id);
        if (index >= 0) {
          state.queue.items[index] = cloneQueueItem(action.payload);
        }
      })
      .addCase(fetchQueueItem.failure, (state, action) => {
        state.detail.phase = 'failed';
        state.detail.error = action.payload;
      })
      // Karar
      .addCase(decideModeration.request, (state) => {
        state.mutation = { phase: 'loading', error: null };
      })
      .addCase(decideModeration.success, (state, action) => {
        state.mutation.phase = 'succeeded';
        state.detail.item = cloneDetail(action.payload);
        const index = state.queue.items.findIndex((item) => item.id === action.payload.id);
        if (index >= 0) {
          state.queue.items[index] = cloneQueueItem(action.payload);
        }
      })
      .addCase(decideModeration.failure, (state, action) => {
        state.mutation = { phase: 'failed', error: action.payload };
      })
      // Sinyal koşusu
      .addCase(runSignals.request, (state) => {
        state.mutation = { phase: 'loading', error: null };
      })
      .addCase(runSignals.success, (state, action) => {
        state.mutation.phase = 'succeeded';
        state.lastSignalRun = action.payload.enqueued;
      })
      .addCase(runSignals.failure, (state, action) => {
        state.mutation = { phase: 'failed', error: action.payload };
      })
      // Genel bakış
      .addCase(fetchOverview.request, (state) => {
        state.overview.phase = 'loading';
        state.overview.error = null;
      })
      .addCase(fetchOverview.success, (state, action) => {
        state.overview.phase = 'succeeded';
        state.overview.data = {
          kpis: { ...action.payload.kpis },
          signals: action.payload.signals.map((signal) => ({ ...signal })),
          health: action.payload.health.map((metric) => ({ ...metric })),
        };
      })
      .addCase(fetchOverview.failure, (state, action) => {
        state.overview.phase = 'failed';
        state.overview.error = action.payload;
      })
      // Kurum doğrulama kuyruğu (Story 18.4)
      .addCase(fetchVerificationQueue.request, (state, action) => {
        state.verificationQueue.phase = 'loading';
        state.verificationQueue.error = null;
        state.verificationQueue.status = action.payload.status;
      })
      .addCase(fetchVerificationQueue.success, (state, action) => {
        const data = action.payload;
        state.verificationQueue.phase = 'succeeded';
        state.verificationQueue.items = data.items.map((item) => ({ ...item }));
        state.verificationQueue.page = data.page;
        state.verificationQueue.total = data.total;
        state.verificationQueue.hasNext = data.hasNext;
      })
      .addCase(fetchVerificationQueue.failure, (state, action) => {
        state.verificationQueue.phase = 'failed';
        state.verificationQueue.error = action.payload;
      })
      .addCase(decideOrganizationVerification.request, (state) => {
        state.mutation = { phase: 'loading', error: null };
      })
      .addCase(decideOrganizationVerification.success, (state, action) => {
        state.mutation.phase = 'succeeded';
        // Karara bağlanan başvuru BEKLEYEN kuyruğundan düşer (liste ayrıca tazelenir).
        state.verificationQueue.items = state.verificationQueue.items.filter(
          (item) => item.requestId !== action.payload.requestId,
        );
        state.verificationQueue.total = Math.max(0, state.verificationQueue.total - 1);
      })
      .addCase(decideOrganizationVerification.failure, (state, action) => {
        state.mutation = { phase: 'failed', error: action.payload };
      })
      .addCase(fetchVerificationDocument.request, (state, action) => {
        state.verificationDocument = {
          requestId: action.payload,
          url: null,
          expiresAt: null,
          phase: 'loading',
          error: null,
        };
      })
      .addCase(fetchVerificationDocument.success, (state, action) => {
        state.verificationDocument.phase = 'succeeded';
        state.verificationDocument.url = action.payload.url;
        state.verificationDocument.expiresAt = action.payload.expiresAt;
      })
      .addCase(fetchVerificationDocument.failure, (state, action) => {
        state.verificationDocument.phase = 'failed';
        state.verificationDocument.error = action.payload;
      });
  },
});

/** Alanlar AÇIKÇA seçilir: detay DTO'su verildiğinde facts/reportNotes listeye sızmaz. */
function cloneQueueItem(item: ModerationQueueItemDto): QueueItemState {
  return {
    id: item.id,
    itemType: item.itemType,
    status: item.status,
    priority: item.priority,
    source: item.source,
    reason: item.reason,
    reportCount: item.reportCount,
    decision: item.decision,
    decisionReason: item.decisionReason,
    decidedAt: item.decidedAt,
    createdAt: item.createdAt,
    updatedAt: item.updatedAt,
    target: { ...item.target },
  };
}

function cloneDetail(item: ModerationQueueItemDetailDto): QueueDetailState {
  return {
    ...item,
    target: { ...item.target },
    facts: item.facts.map((fact) => ({ ...fact })),
    reportNotes: [...item.reportNotes],
  };
}

export const { usersFilterChanged, queueItemSelected, mutationReset } = slice.actions;
export const adminReducer = slice.reducer;
